import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { AbcCore, CollectionHelper, CompareHelper } from '@barthauer/abc/core';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import 'hammerjs';
import { ResourceSet } from 'libs/abc/core/resources/src/lib/resource-set';
import { of, Subscription } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { MapSelection } from './map.selection';
import { ILegendSettings, LegendSettings } from './models/legend-settings.model';
import { LayerItemFlatNode, LayerItemNode } from './models/legend.model';
import { LegendSettingsService } from './services/legend-settings.service';
import { LegendService } from './services/legend.service';
import { MapService } from './services/map.service';
import { MapLayer, MapStyle } from './models/map.model';
import { AbcPopupDialogService } from '@barthauer/abc/messages';
import {
  DialogResult,
  AbcPopupDialogButtonCombination,
  AbcPopupDialogSettings,
} from 'libs/abc/messages/src/lib/popup-dialog/abc-popup-dialog.model';
declare let ol: any;

@Component({
  selector: 'legend-component',
  templateUrl: './legend.component.html',
  styleUrls: ['./legend.component.scss'],
  providers: [LegendService],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class LegendComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('rtlEnabled') rtlEnabled: boolean;
  dataSourceClone: LayerItemNode[];
  searchText: string;
  allAutocompleteItems: string[];
  searchInput: string;
  hasData: boolean;
  isFiltering = false;
  private subscription: Subscription[] = [];
  resizeLegendHammerEl: any;
  hammer: any;
  LegendHtmlElement: HTMLElement;
  startWidth: number;
  displayTooltip: boolean;
  parentElement: HTMLElement;
  currentElement: HTMLElement;
  private currentSettings: ILegendSettings;
  isMobileEnv: boolean = false;

  async ngOnInit() {
    this.res = await this.resAsync;
  }

  ngAfterViewInit() {
    this.resizeLegendHammerEl = document.querySelector('.resizeLegend');
    this.LegendHtmlElement = document.querySelector('.slideNav') as HTMLElement;
    const widthValue = JSON.parse(localStorage.getItem('legendWidth'));
    this.LegendHtmlElement.style.width = widthValue;

    if (this.resizeLegendHammerEl) {
      this.hammer = new Hammer(this.resizeLegendHammerEl);
      this.hammer.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL });
      this.hammer.on('panstart', (event: any) => {
        this.legendPanStart(event);
      });

      this.hammer.on('panright panleft', (event: any) => {
        this.legendPanMove(event);
      });
    }
  }

  legendPanStart(e) {
    this.startWidth = this.LegendHtmlElement.offsetWidth;
  }

  legendPanMove(e) {
    this.LegendHtmlElement.style.width = this.startWidth + e.deltaX + 'px';
    localStorage.setItem('legendWidth', JSON.stringify(this.LegendHtmlElement.style.width));
  }
  ngOnDestroy() {
    this.subscription.forEach((element) => {
      element.unsubscribe();
    });
  }
  mapSelection: MapSelection;

  nestedNodeMap = new Map<LayerItemNode, LayerItemFlatNode>();

  treeControl: FlatTreeControl<LayerItemFlatNode>;

  treeFlattener: MatTreeFlattener<LayerItemNode, LayerItemFlatNode>;

  dataSource: MatTreeFlatDataSource<LayerItemNode, LayerItemFlatNode>;
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;

  constructor(
    private database: LegendService,
    private settingsService: LegendSettingsService,
    private resourceService: ResourceManagerService,
    private abcPopupDialogService: AbcPopupDialogService,
    private mapService: MapService,
    private abcCore: AbcCore,
    private changeDetectorRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<LayerItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this.subscription.push(database.mapLoaded.subscribe((_) => (this.mapSelection = _)));
    this.subscription.push(
      database.dataChange
        .pipe(
          concatMap((data) => {
            if (CompareHelper.isEmptyObject(data)) return of(data);
            return this.settingsService.getCurrentSettings().pipe(
              map((settings) => {
                if (!settings) {
                  settings = new LegendSettings();
                }
                this.currentSettings = settings;
                if (!this.initializeNodesWithSettings(data, settings)) {
                  this.settingsService.removeCurrentSettings();
                }
                return data;
              }),
            );
          }),
        )
        .subscribe(
          (data) => {
            this.dataSourceClone = data;
            this.dataSource.data = data;

            this.expandNodes(data);

            this.hasData = !!data?.length;

            if (data?.length) {
              this.mapService.saveLegendState = () => this.settingsService.setCurrentSettings(this.currentSettings);
              this.mapService.notifyLegendInitialized();
            }
          },
          (error) => {
            this.abcCore.error.publish(error);
          },
        ),
    );

    this.subscription.push(
      this.mapService.onMobileEnvironment().subscribe((isMobile) => {
        this.isMobileEnv = isMobile;
      }),
    );

    this.resAsync = this.resourceService.getResourceSet('abc-graphics-adapters-open-layers');

    this.mapService.getVisibleSimpleLayerNames = async () => this.getVisibleLeafLayerNames();
  }

  private expandNodes(layers: LayerItemNode[]) {
    if (layers) {
      for (const layer of layers) {
        const n = this.nestedNodeMap.get(layer);
        if (n) {
          const savedExpansion = this.currentSettings?.expansion?.[n.key];
          if (savedExpansion !== undefined) {
            if (savedExpansion) {
              this.treeControl.expand(n);
            } else {
              this.treeControl.collapse(n);
            }
          } else {
            this.treeControl.expand(n);
            if (layer.collapsedByDefault === true) {
              this.treeControl.collapse(n);
            }
          }
        }
        this.expandNodes(layer.children);
      }
    }
  }

  getLevel = (node: LayerItemFlatNode) => node.level;

  isExpandable = (node: LayerItemFlatNode) => node.expandable;

  getChildren = (node: LayerItemNode): LayerItemNode[] => node.children;

  hasChild = (_: number, _nodeData: LayerItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: LayerItemFlatNode) => _nodeData.key === '';

  transformer = (node: LayerItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.key === node.key ? existingNode : new LayerItemFlatNode(node);
    flatNode.level = level;

    this.nestedNodeMap.set(node, flatNode);

    return flatNode;
  };

  getVisibleLeafLayerNames(): string[] {
    const names: string[][] = (this.treeControl.dataNodes ?? [])
      .filter(
        (n) =>
          n.node.key !== '---' &&
          (n.node.children?.filter((_) => !_.style).length ?? 0) === 0 &&
          this.isNodeEffectivelyVisible(n),
      )
      .map((n) => this.getFullLayerNames(n.layer?.layerdefinition));
    return CollectionHelper.flatten(names);
  }
  
  private getFullLayerNames(layer: MapLayer): string[] {
    const result: string[] = [];
    this.appendFullLayerNames(result, layer);
    return result;
  }

  private appendFullLayerNames(names: string[], layer: MapLayer): void {
    if (typeof layer?.fullName === 'string') {
      names.push(layer.fullName);
    } else if (layer?.layers?.length) {
      for (const ly of layer.layers) {
        this.appendFullLayerNames(names, ly);
      }
    }
  }

  isNodeLayerVisible(node: LayerItemFlatNode): boolean {
    return this.isLayerVisible(node?.layer, node?.node?.style) ?? (node?.node?.style ? false : true);
  }

  isAnyNodeLayerVisible(nodes: LayerItemFlatNode[]): boolean {
    for (const node of nodes) {
      if (this.isNodeLayerVisible(node)) {
        return true;
      }
    }
    return false;
  }

  isNodeEffectivelyVisible(node: LayerItemFlatNode): boolean {
    let cur: LayerItemFlatNode | undefined = node;

    while (cur) {
      const layer = cur.layer;
      if (!layer || layer.getVisible?.() !== true) return false;
      cur = this.getParentNode(cur);
    }

    return true;
  }

  isLayerVisible(layer: any, style: MapStyle): boolean {
    if (layer?.getVisible()) {
      if (layer.getLayers) {
        const arr = layer.getLayers().getArray();
        if (arr.length > 0) {
          for (const l of arr) {
            if (this.isLayerVisible(l, null)) {
              return true;
            }
          }
          return false;
        } else {
          return !style;
        }
      } else {
        return !style;
      }
    } else if (layer) {
      return false;
    } else if (style) {
      return false;
    }
  }

  private descendantsAllSelected(node: LayerItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.every((child) => this.isNodeLayerVisible(child));
    return descAllSelected;
  }

  private descendantsAnySelected(node: LayerItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) => this.isNodeLayerVisible(child));
    return result;
  }

  descendantsPartiallySelected(node: LayerItemFlatNode): boolean {
    return this.descendantsAnySelected(node) && !this.descendantsAllSelected(node);
  }

  toggleLayerItemSelection(node: LayerItemFlatNode): void {
    const visible: boolean = node?.layer?.getVisible();
    this.updateLayerVisibility(node, !visible);
  }

  toggleLayerItemSelectionEx(node: LayerItemFlatNode, e: MatCheckboxChange): void {
    const visible = e.checked;
    this.updateLayerVisibility(node, visible);
  }

  getParentNode(node: LayerItemFlatNode, allnodes?: LayerItemFlatNode[]): LayerItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }
    if (!allnodes) {
      allnodes = this.treeControl.dataNodes;
    }

    const startIndex = allnodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = allnodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }
  getRootNode(node: LayerItemFlatNode, allnodes?: LayerItemFlatNode[]): LayerItemFlatNode | null {
    let parent: LayerItemFlatNode | null = node;
    let current: LayerItemFlatNode | null;
    do {
      current = parent;
      parent = this.getParentNode(current, allnodes);
    } while (parent);
    return current;
  }

  getRootNodes(nodes: LayerItemFlatNode[]): LayerItemFlatNode[] {
    const roots: LayerItemFlatNode[] = [];
    for (const node of nodes) {
      const root = this.getRootNode(node, nodes);
      if (roots.indexOf(root) < 0) {
        roots.push(root);
      }
    }
    return roots;
  }

  resetFilter(): void {
    this.applyFilterCore('', false);
  }

  applyFilter(filterValue: string): void {
    this.applyFilterCore(filterValue, true);
  }

  private applyFilterCore(filterValue: string, isFiltering: boolean): void {
    this.searchInput = filterValue;
    this.isFiltering = isFiltering;
    if (filterValue) {
      this.dataSource.data = this.applyFilterTree(this.dataSourceClone, filterValue?.toLocaleLowerCase());
    } else {
      this.dataSource.data = this.dataSourceClone;
    }

    this.expandNodes(this.dataSource.data);
    if (this.dataSource.data.length != this.dataSourceClone.length) {
      this.treeControl.expandAll();
    }
    this.hasData = !!this.dataSource.data?.length;
  }
  private applyFilterTree(data: LayerItemNode[], filterText: string) {
    return !Array.isArray(data)
      ? null
      : data.reduce((list: LayerItemNode[], entry: LayerItemNode) => {
          let clone: LayerItemNode = null;
          if (entry.children != null) {
            if (entry.key.toLocaleLowerCase().includes(filterText)) {
              clone = Object.assign({}, entry);
            }

            const _children = this.applyFilterTree(entry.children, filterText);
            if (Array.isArray(_children))
              if (_children.length > 0) {
                clone = Object.assign({}, entry, { children: _children });
              }
          } else if (entry.key.toLocaleLowerCase().includes(filterText)) {
            clone = Object.assign({}, entry);
          }
          clone && list.push(clone);
          return list;
        }, []);
  }

  mapZoomToLayer(node: LayerItemFlatNode): void {
    try {
      if (node.layer !== null) {
        this.mapService.mapZoomToLayerView(node);
      }
    } catch (e) {
      this.mapService.sendMapShortMessage(e.message);
    }
  }

  mapOpenDataTable(node: LayerItemFlatNode): void {
    try {
      this.mapService.mapOpenDataTable(node);
    } catch (e) {
      this.mapService.sendMapShortMessage(e.message);
    }
  }

  canMapZoomToLayer(node: LayerItemFlatNode): boolean {
    try {
      if (node.layer !== null) {
        return this.mapSelection?.canZoomToLayer(node.layer);
      }
    } catch (e) {
      this.mapService.sendMapShortMessage(e.message);
    }
  }

  canMapOpenDataTable(node: LayerItemFlatNode): boolean {
    try {
      return (
        this.mapService.canOpenDataTable &&
        this.mapService.canOpenDataTable(
          node?.layer?.layerdefinition?.templateName ?? node?.layer?.layerdefinition?.fullName,
        )
      );
    } catch (e) {
      this.mapService.sendMapShortMessage(e.message);
    }
  }
  toggleDefault() {
    this.displayTooltip = false;
  }

  isEllipsisActive(node) {
    this.currentElement = document.getElementById(node.item)?.querySelector('.mdc-label');
    this.parentElement = document.getElementById(node.item)?.querySelector('.span-ellipsis');
    if (this.parentElement && this.currentElement) {
      if (document.getElementById(node.item)?.querySelector('.mat-button-wrapper')) {
        this.displayTooltip = this.parentElement.offsetWidth <= this.currentElement.offsetWidth - 24;
      } else {
        this.displayTooltip = this.parentElement.offsetWidth <= this.currentElement.offsetWidth;
      }
    }
  }

  private updateLayerVisibility(node: LayerItemFlatNode, visible: boolean) {
    if (node?.layer) {
      node.layer.setVisible(visible);

      //debug the tree node, it's useful
      // if (this.mapSelection?.getMap()) {
      //   const mp = this.mapSelection.getMap();
      //   this.loopMapLayers(mp.getLayerGroup());
      // }

      if (!this.currentSettings) {
        this.currentSettings = new LegendSettings();
      }

      if (this.currentSettings.visibility[node.key] !== visible) {
        this.currentSettings.visibility[node.key] = visible;
        this.settingsService.setCurrentSettings(this.currentSettings).subscribe(
          () => {},
          (error) => {
            this.abcCore.error.publish(error);
          },
        );
      }
    }
  }

  private initializeNodesWithSettings(data: LayerItemNode[], settings: ILegendSettings): boolean {
    const nodes = this.treeFlattener.flattenNodes(data);
    const visibility = settings.visibility;
    let settingsApplied = false;
    if (!CompareHelper.isEmptyObject(visibility)) {
      let foundAll = true;
      for (const n of nodes) {
        const visible = n.layer?.layerdefinition?.visibleInLegend !== false;
        if (visible) {
          if (visibility[n.key] === undefined) {
            foundAll = false;
            break;
          }
        }
      }
      if (foundAll) {
        for (const n of nodes) {
          const visible = visibility[n.key];
          if (visible !== undefined) {
            n.layer?.setVisible(visible);
          }
        }
        settingsApplied = true;
      }
    }
    if (!settingsApplied || (nodes.length && !this.isAnyNodeLayerVisible(this.getRootNodes(nodes)))) {
      for (const n of nodes) {
        const visible = n.layer?.layerdefinition?.visible !== false;
        visibility[n.key] = visible;
        n.layer?.setVisible(visible);
      }
      return false;
    }
    return true;
  }
  toggleNodeExpansion(node: LayerItemFlatNode): void {
    this.treeControl.toggle(node);
    this.saveNodeExpansionState(node);
  }

  private saveNodeExpansionState(node: LayerItemFlatNode): void {
    if (!this.currentSettings) {
      this.currentSettings = new LegendSettings();
    }

    const isExpanded = this.treeControl.isExpanded(node);

    if (this.currentSettings.expansion[node.key] !== isExpanded) {
      this.currentSettings.expansion[node.key] = isExpanded;
      this.settingsService.setCurrentSettings(this.currentSettings).subscribe(
        () => {},
        (error) => {
          this.abcCore.error.publish(error);
        },
      );
    }
  }

  async resetToDefaults(): Promise<void> {
    const settings = new AbcPopupDialogSettings();
    settings.headerTitle = this.res.get('Legend-Tooltip');
    settings.buttonCombination = AbcPopupDialogButtonCombination.Custom;
    settings.customButtons = [
      {
        text: this.res.get('Yes'),
        dialogResult: DialogResult.Yes,
        cssClass: 'abc-popup-dialog-toolbarItem',
        elementAttr: { class: 'abc-popup-dialog-button-yes' },
      },
      {
        text: this.res.get('No'),
        dialogResult: DialogResult.No,
        cssClass: 'abc-popup-dialog-toolbarItem',
        elementAttr: { class: 'abc-popup-dialog-button-no' },
      },
    ];
    if (window.innerWidth < 400) {
      settings.width = 350;
    } else if (window.innerWidth < 640) {
      settings.width = 400;
    } else {
      settings.width = 500;
    }

    const result = await this.abcPopupDialogService.showQuestionDialog(this.res.get('Legend-ResetSetting'), settings);
    if (result === DialogResult.Yes) {
      this.settingsService.removeCurrentSettings();
      this.currentSettings = new LegendSettings();

      const nodes = this.treeFlattener.flattenNodes(this.dataSourceClone);
      for (const n of nodes) {
        const visible = n.layer?.layerdefinition?.visible !== false;
        n.layer?.setVisible(visible);
        this.currentSettings.visibility[n.key] = visible;
      }

      this.resetExpansionToDefaults(this.dataSourceClone);

      this.settingsService.setCurrentSettings(this.currentSettings).subscribe(
        () => {},
        (error) => {
          this.abcCore.error.publish(error);
        },
      );
    }
  }

  private resetExpansionToDefaults(layers: LayerItemNode[]): void {
    if (!layers) return;

    for (const layer of layers) {
      const n = this.nestedNodeMap.get(layer);
      if (n) {
        const isExpanded = layer.collapsedByDefault !== true;
        if (isExpanded) {
          this.treeControl.expand(n);
        } else {
          this.treeControl.collapse(n);
        }
        this.currentSettings.expansion[n.key] = isExpanded;
      }
      this.resetExpansionToDefaults(layer.children);
    }
  }

  protected sanitize(svgContent: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svgContent);
  }
}
